import React from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import SwipeButton from "../components/SwipeButton";
import { useBooking } from "../context/BookingContext";
import ga_logo from "../assets/images/ga_logo.png";
import { useAppLanguage } from "../hooks/useAppLanguage";
import { useNavigate } from "react-router-dom";
import { fetchDoorAccess, openDoorAccess } from "../services/reservationService";
import {
  canUseDigitalKey,
  getRoomAccessMessage,
  getStoredRoomAccess,
  hasNonBlockingRoomAccessPayment,
  isRoomAccessPaymentBlocking,
  normalizeRoomAccess,
  storeRoomAccess,
} from "../utils/roomAccess";
import { canUseCapability } from "../utils/journeyAccess";

const digitalKeyTranslations = {
  de: {
    language: "Sprache",
    eyebrow: "Privater Zugang",
    title: "Digitaler Schlüssel",
    subtitle: "Öffnen Sie Ihre Hotelbereiche mit einer einfachen Wischbewegung.",
    frontDoor: "Haupteingang",
    frontDoorCopy: "Zugang zum Hotel",
    room: "Zimmer",
    roomAvailable: "Zimmerzugang ist verfügbar",
    roomUnavailable: "Bitte klären Sie den Zimmerzugang mit der Rezeption",
    accessUnavailable: "Der digitale Schlüssel ist noch nicht verfügbar.",
    doorAccessLoading: "Türzugang wird geladen...",
    doorAccessUnavailable: "Türzugang ist nicht verfügbar.",
    doorSystemUnavailable: "Das Türsystem ist vorübergehend nicht verfügbar. Bitte wenden Sie sich an das Personal.",
    genericDoorError: "Die Tür konnte nicht geöffnet werden. Bitte versuchen Sie es erneut oder wenden Sie sich an das Personal.",
    noDoors: "Für diese Reservierung sind keine Türen verfügbar.",
    paymentRequiredTitle: "Zahlung erforderlich",
    paymentRequired: "Zahlung erforderlich, bevor der Zimmerzugang aktiviert wird.",
    payBalance: "Zum Checkout",
    nonBlockingPaymentTitle: "Offener Betrag",
    nonBlockingPayment: "Die Zahlung ist noch offen, Ihr Türzugang bleibt jedoch verfügbar.",
    backHome: "Zurück zur Startseite",
    testOverrideActive: "Test-Override aktiv",
    doorPin: "Tür-PIN",
    pinValid: "Gültig",
    pinValidityNote: "Dieser PIN funktioniert nur während des gültigen Aufenthaltszeitraums.",
    openDoor: "Tür öffnen",
    openingDoor: "Öffnet...",
    doorOpeningRequested: "Türöffnung angefragt",
    roomDoor: "Zimmer",
    commonDoor: "Hotelbereich",
    doorLabels: {
      Nachteingang: "Nachteingang",
      "Eingang Fitness": "Eingang Fitness",
      Tiefgarageneinfahrt: "Tiefgarageneinfahrt",
    },
    blockerMessages: {
      ONLINE_CHECKIN_REQUIRED: "Bitte schließen Sie zuerst den Online Check-in ab.",
      ARRIVAL_TIME_NOT_REACHED: "Wenn Sie früher Zugang zu Ihrem Zimmer wünschen, wenden Sie sich bitte an die Rezeption.",
      PAYMENT_REQUIRED: "Bitte begleichen Sie zuerst den offenen Betrag.",
      PAYMENT_STATUS_UNVERIFIED: "Der Zahlungsstatus konnte gerade nicht geprüft werden.",
      PAYMENT_STATUS_PENDING: "Der Zahlungsstatus ist noch nicht verfügbar.",
      RESERVATION_EXPIRED: "Der Zugang ist abgelaufen.",
    },
  },
  en: {
    language: "Language",
    eyebrow: "Private Access",
    title: "Digital Key",
    subtitle: "Unlock your hotel spaces with a single gesture.",
    frontDoor: "Front Door",
    frontDoorCopy: "Main hotel access",
    room: "Room",
    roomAvailable: "Guest room access is available",
    roomUnavailable: "Please verify room access with reception",
    accessUnavailable: "Digital key access is not available yet.",
    doorAccessLoading: "Loading door access...",
    doorAccessUnavailable: "Door access is not available.",
    doorSystemUnavailable: "Door system is temporarily unavailable. Please contact staff.",
    genericDoorError: "The door could not be opened. Please try again or contact staff.",
    noDoors: "No doors are available for this reservation.",
    paymentRequiredTitle: "Payment required",
    paymentRequired: "Payment is required before room access can be activated.",
    payBalance: "Go to checkout",
    nonBlockingPaymentTitle: "Outstanding balance",
    nonBlockingPayment: "Payment is still due, but your door access remains available.",
    backHome: "Back to home",
    testOverrideActive: "Test override active",
    doorPin: "Door PIN",
    pinValid: "Valid",
    pinValidityNote: "This PIN works only during the valid stay period.",
    openDoor: "Open door",
    openingDoor: "Opening...",
    doorOpeningRequested: "Door opening requested",
    roomDoor: "Room",
    commonDoor: "Hotel area",
    doorLabels: {
      Nachteingang: "Night entrance",
      "Eingang Fitness": "Fitness entrance",
      Tiefgarageneinfahrt: "Underground garage entrance",
    },
    blockerMessages: {
      ONLINE_CHECKIN_REQUIRED: "Please complete online check-in first.",
      ARRIVAL_TIME_NOT_REACHED: "If you would like earlier access to your room, please visit reception.",
      PAYMENT_REQUIRED: "Please settle the open balance first.",
      PAYMENT_STATUS_UNVERIFIED: "Payment status could not be verified right now.",
      PAYMENT_STATUS_PENDING: "Payment status is not available yet.",
      RESERVATION_EXPIRED: "Access has expired.",
    },
  },
};

const normalizeDoorAccess = (doorAccess) => {
  const source = doorAccess && typeof doorAccess === "object" ? doorAccess : {};

  return {
    ...source,
    enabled: source.enabled === true,
    supportedMode: source.supportedMode || source.supported_mode || "",
    pinCodeCreationSupported: source.pinCodeCreationSupported === true || source.pin_code_creation_supported === true,
    hasActiveAccess: source.hasActiveAccess === true || source.has_active_access === true,
    hasActiveCredential: source.hasActiveCredential === true || source.has_active_credential === true,
    credential: source.credential || null,
    blockers: Array.isArray(source.blockers) ? source.blockers : [],
    doors: Array.isArray(source.doors) ? source.doors : [],
  };
};

const getDoorId = (door) => door?.id;
const getDoorKey = (door) => door?.doorKey || door?.door_key;
const getDoorRequestKey = (door) => {
  const doorId = getDoorId(door);
  return doorId !== undefined && doorId !== null && doorId !== "" ? doorId : getDoorKey(door);
};
const getDoorType = (door) => door?.doorType || door?.door_type;
const normalizeDoorDisplayKey = (value) => String(value || "").replace(/\*/g, "").trim();
const getDoorLabel = (door, t) => {
  const doorKeyLabel = normalizeDoorDisplayKey(getDoorKey(door));
  const backendLabel = normalizeDoorDisplayKey(door?.label);
  const translatedLabel = t.doorLabels?.[doorKeyLabel] || t.doorLabels?.[backendLabel];

  return translatedLabel || backendLabel || doorKeyLabel || "";
};
const hasDoorRequestKey = (door) => {
  const requestKey = getDoorRequestKey(door);
  return requestKey !== undefined && requestKey !== null && requestKey !== "";
};

const isDoorActive = (door) => {
  const status = String(door?.status || "ACTIVE").toUpperCase();
  return status === "ACTIVE";
};

const canUseRemoteOpenMode = (doorAccess) =>
  ["REMOTE_OPEN", "REMOTE_OPEN_AND_PIN"].includes(doorAccess.supportedMode);

const formatDateTime = (value, language) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(language === "en" ? "en-US" : "de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatErrorList = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    return Object.values(value).map(formatErrorList).filter(Boolean).join(" ");
  }
  return String(value || "").trim();
};

const getLocalizedRoomAccessMessage = (roomAccess, t, fallback = "") => {
  const normalizedRoomAccess = normalizeRoomAccess(roomAccess);
  const codes = Array.isArray(normalizedRoomAccess.blocker_codes) ? normalizedRoomAccess.blocker_codes : [];
  const translated = codes
    .map((code) => t.blockerMessages?.[String(code || "").toUpperCase()])
    .filter(Boolean);

  if (translated.length > 0) {
    return translated.join(" ");
  }

  return getRoomAccessMessage(normalizedRoomAccess, fallback);
};

const getBackendBlockerMessage = (data, t) => {
  const roomAccess = data?.roomAccess || data?.room_access;
  if (roomAccess) {
    const message = getLocalizedRoomAccessMessage(roomAccess, t, "");
    if (message) return message;
  }

  return (
    formatErrorList(data?.errors?.roomAccess) ||
    formatErrorList(data?.errors?.room_access) ||
    formatErrorList(data?.error) ||
    formatErrorList(data?.message)
  );
};

const getDoorAccessErrorMessage = (error, t) => {
  const status = error?.response?.status;
  const data = error?.response?.data || error || {};

  if (status === 502) {
    return t.doorSystemUnavailable;
  }

  const blockerMessage = getBackendBlockerMessage(data, t);
  if (status === 403 && blockerMessage) {
    return blockerMessage;
  }

  if (data?.ok === false && blockerMessage) {
    return blockerMessage;
  }

  return t.genericDoorError;
};

const DigitalKeyPage = () => {
  const booking = useBooking();
  const navigate = useNavigate();
  const [language, setLanguage] = useAppLanguage();
  const t = digitalKeyTranslations[language] || digitalKeyTranslations.de;
  const [loadingDoorAccess, setLoadingDoorAccess] = React.useState(true);
  const [openingDoorId, setOpeningDoorId] = React.useState(null);
  const [successDoorId, setSuccessDoorId] = React.useState(null);
  const [notice, setNotice] = React.useState({ type: "", text: "" });
  const [roomAccess, setRoomAccess] = React.useState(() =>
    normalizeRoomAccess(booking["room-access"] || getStoredRoomAccess())
  );
  const [doorAccess, setDoorAccess] = React.useState(() => normalizeDoorAccess(null));
  const isMountedRef = React.useRef(true);
  const refreshInFlightRef = React.useRef(null);
  const paymentBlocksAccess = isRoomAccessPaymentBlocking(roomAccess);
  const nonBlockingPaymentRequired = hasNonBlockingRoomAccessPayment(roomAccess);
  const canUseOwnerCheckout =
    canUseCapability("checkout") || canUseCapability("payment");
  const doorControlsAllowed =
    canUseDigitalKey(roomAccess) &&
    doorAccess.enabled === true &&
    canUseRemoteOpenMode(doorAccess) &&
    doorAccess.hasActiveAccess === true;
  const credential = doorAccess.credential || {};
  const pinCode = credential.pinCode || credential.pin_code || "";
  const pinCanBeShown =
    roomAccess.enabled === true &&
    roomAccess.can_show_pin === true &&
    doorAccess.enabled === true &&
    doorAccess.pinCodeCreationSupported === true &&
    doorAccess.hasActiveCredential === true &&
    String(credential.status || "").toUpperCase() === "ACTIVE" &&
    Boolean(pinCode);
  const pinValidFrom = formatDateTime(credential.validFrom || credential.valid_from, language);
  const pinValidUntil = formatDateTime(credential.validUntil || credential.valid_until, language);
  const accessBlockedMessage = getLocalizedRoomAccessMessage(
    roomAccess,
    t,
    paymentBlocksAccess ? t.paymentRequired : t.accessUnavailable
  );

  const applyRoomAccess = React.useCallback((nextRoomAccess) => {
    const normalized = storeRoomAccess(nextRoomAccess);
    setRoomAccess(normalized);
    window.dispatchEvent(new Event("booking-update"));
    return normalized;
  }, []);

  const refreshDoorAccess = React.useCallback((showLoading = false) => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    if (showLoading) setLoadingDoorAccess(true);

    const request = fetchDoorAccess()
      .then((data) => {
        if (!isMountedRef.current) return;

        if (data.roomAccess) {
          applyRoomAccess(data.roomAccess);
        }
        setDoorAccess(normalizeDoorAccess(data.doorAccess));
      })
      .catch((error) => {
        if (!isMountedRef.current) return;

        const data = error?.response?.data;
        const responseRoomAccess = data?.roomAccess || data?.room_access;
        if (responseRoomAccess) {
          applyRoomAccess(responseRoomAccess);
        }
        const responseDoorAccess = data?.doorAccess || data?.door_access;
        if (responseDoorAccess) {
          setDoorAccess(normalizeDoorAccess(responseDoorAccess));
        }
        setNotice({ type: "error", text: getDoorAccessErrorMessage(error, t) });
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoadingDoorAccess(false);
        }
        if (refreshInFlightRef.current === request) refreshInFlightRef.current = null;
      });

    refreshInFlightRef.current = request;
    return request;
  }, [applyRoomAccess, t]);

  React.useEffect(() => {
    isMountedRef.current = true;
    setNotice({ type: "", text: "" });
    refreshDoorAccess(true);

    const refreshOnResume = () => refreshDoorAccess(false);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshDoorAccess(false);
    };

    window.addEventListener("focus", refreshOnResume);
    window.addEventListener("pageshow", refreshOnResume);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener("focus", refreshOnResume);
      window.removeEventListener("pageshow", refreshOnResume);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshDoorAccess]);

  const getDisabledMessage = () => {
    if (!canUseDigitalKey(roomAccess)) {
      return accessBlockedMessage;
    }
    if (doorAccess.message) {
      return doorAccess.message;
    }
    if (doorAccess.blockers.length > 0) {
      return doorAccess.blockers.join(" ");
    }
    return t.doorAccessUnavailable;
  };

  const updateOpenedDoor = (openedDoor) => {
    if (!openedDoor?.id) return;

    setDoorAccess((currentDoorAccess) => ({
      ...currentDoorAccess,
      doors: currentDoorAccess.doors.map((door) =>
        door.id === openedDoor.id ? { ...door, ...openedDoor } : door
      ),
    }));
  };

  const handleOpenDoor = async (door) => {
    if (!doorControlsAllowed || !isDoorActive(door)) {
      return;
    }

    const doorRequestKey = getDoorRequestKey(door);
    setOpeningDoorId(doorRequestKey);
    setSuccessDoorId(null);
    setNotice({ type: "", text: "" });

    try {
      const result = await openDoorAccess(door);
      if (result.roomAccess) {
        applyRoomAccess(result.roomAccess);
      }

      if (result.ok === false) {
        setNotice({ type: "error", text: getDoorAccessErrorMessage(result, t) });
        return;
      }

      const responseDoorAccess = normalizeDoorAccess(result.doorAccess);
      if (responseDoorAccess.doors.length > 0) {
        setDoorAccess(responseDoorAccess);
      } else {
        updateOpenedDoor(result.doorAccess);
      }

      setSuccessDoorId(doorRequestKey);
      setNotice({ type: "success", text: t.doorOpeningRequested });
    } catch (error) {
      const data = error?.response?.data;
      const responseRoomAccess = data?.roomAccess || data?.room_access;
      if (error?.response?.status === 403) {
        applyRoomAccess(responseRoomAccess || {});
        setDoorAccess(normalizeDoorAccess(data?.doorAccess || data?.door_access));
      } else if (responseRoomAccess) {
        applyRoomAccess(responseRoomAccess);
      }
      setNotice({ type: "error", text: getDoorAccessErrorMessage(error, t) });
    } finally {
      setOpeningDoorId(null);
    }
  };

  const doors = doorAccess.doors;
  const isAnyDoorOpening = openingDoorId !== null;
 
  return (
    <>
      <div className="digital-key-page">
        <header className="digital-key-header">
          <div className="page-header-top">
            <img src={ga_logo} alt="guestified grand airport hotel logo" className="digital-key-logo" />
            <label className="home-language-switcher">
              <span className="visually-hidden">{t.language}</span>
              <select value={language} onChange={(event) => setLanguage(event.target.value)}>
                <option value="de">DE</option>
                <option value="en">EN</option>
              </select>
            </label>
          </div>
          {/* <p className="digital-key-eyebrow">{t.eyebrow}</p>
          <h1>{t.title}</h1>
          <p className="digital-key-subtitle">{t.subtitle}</p> */}
          {/* {testOverrideActive && (
            <span className="digital-key-test-badge">{t.testOverrideActive}</span>
          )} */}
        </header>

        <main className="digital-key-content">
          {loadingDoorAccess ? (
            <section className="digital-key-card digital-key-card-disabled">
              <div className="digital-key-card-top">
                <h2>{t.doorAccessLoading}</h2>
                <p>{t.subtitle}</p>
              </div>
            </section>
          ) : pinCanBeShown || doorControlsAllowed ? (
            <>
              {nonBlockingPaymentRequired && canUseOwnerCheckout && (
                <section className="digital-key-card digital-key-balance-card">
                  <div className="digital-key-card-top">
                    <h2>{t.nonBlockingPaymentTitle}</h2>
                    <p>{t.nonBlockingPayment}</p>
                  </div>
                  <div className="digital-key-actions">
                    <button className="digital-key-button secondary" type="button" onClick={() => navigate("/checkout")}>
                      {t.payBalance}
                    </button>
                  </div>
                </section>
              )}
              {pinCanBeShown && (
                <section className="digital-key-card digital-key-pin-card">
                  <div className="digital-key-card-top">
                    <h2>{t.doorPin}</h2>
                    <p>{t.pinValidityNote}</p>
                  </div>
                  <div className="digital-key-pin-code" aria-label={t.doorPin}>
                    {pinCode}
                    <span className="digital-key-pin-enter-symbol" aria-hidden="true">↵</span>
                  </div>
                  {(pinValidFrom || pinValidUntil) && (
                    <p className="digital-key-pin-validity">
                      {t.pinValid}: {[pinValidFrom, pinValidUntil].filter(Boolean).join(" - ")}
                    </p>
                  )}
                </section>
              )}
              {doorControlsAllowed && (doors.length > 0 ? doors.map((door, index) => {
                const doorRequestKey = getDoorRequestKey(door);
                const isOpening = openingDoorId === doorRequestKey;
                const wasRequested = successDoorId === doorRequestKey;
                const canOpenDoor = hasDoorRequestKey(door) && isDoorActive(door) && !isAnyDoorOpening;

                return (
                  <section className="digital-key-card" key={doorRequestKey ?? `door-${index}`}>
                    <div className="digital-key-card-top">
                      <h2>{getDoorLabel(door, t)}</h2>
                      <p>{getDoorType(door) === "ROOM" ? t.roomDoor : t.commonDoor}</p>
                    </div>
                    <div className="digital-key-swipe-wrap">
                      <SwipeButton
                        label={isOpening ? t.openingDoor : t.openDoor}
                        onSwipe={() => handleOpenDoor(door)}
                        disabled={!canOpenDoor}
                        error={!wasRequested}
                        successLabel={t.doorOpeningRequested}
                      />
                    </div>
                    <div className="digital-key-actions">
                      {wasRequested && (
                        <p className="digital-key-status success">{t.doorOpeningRequested}</p>
                      )}
                    </div>
                  </section>
                );
              }) : (
                <section className="digital-key-card digital-key-card-disabled">
                  <div className="digital-key-card-top">
                    <h2>{t.roomUnavailable}</h2>
                    <p>{t.noDoors}</p>
                  </div>
                </section>
              ))}
            </>
          ) : (
            <section className="digital-key-card digital-key-card-disabled">
              <div className="digital-key-card-top">
                <h2>{paymentBlocksAccess ? t.paymentRequiredTitle : t.roomUnavailable}</h2>
                <p>{getDisabledMessage()}</p>
              </div>
              <div className="digital-key-actions">
                {paymentBlocksAccess && canUseOwnerCheckout && (
                  <button className="digital-key-button primary" type="button" onClick={() => navigate("/checkout")}>
                    {t.payBalance}
                  </button>
                )}
                <button className="digital-key-button secondary" type="button" onClick={() => navigate("/home")}>
                  {t.backHome}
                </button>
              </div>
            </section>
          )}
          {notice.text && (
            <p className={`digital-key-status ${notice.type}`}>{notice.text}</p>
          )}
        </main>
      </div>
      {isAnyDoorOpening && <div className="digital-key-loading">
        <LoadingSpinner />
      </div>}
    </>
  );
};

export default DigitalKeyPage;
